/**
 * Decoration Texture Generator
 *
 * PHASE 7C: Convert Decorations from Graphics to Sprites
 * Pre-generates all decoration textures at boot time for GPU caching.
 *
 * Performance Impact:
 * - Before: 40-100 decorations × 60 FPS × draw calls = massive CPU load
 * - After: 40-100 sprites × 60 FPS × 1 GPU blit = minimal CPU load
 * - Expected gain: 2-5 FPS on mobile devices (especially Firefox)
 *
 * Decoration Types by Zone:
 * - Zone 1 (Forest): 19 types (firefly, frog, butterfly, etc.)
 * - Zone 2 (Trees): 8 types (bird, nest, berries, etc.)
 * - Zone 3 (Sky): 9 types (clouds, feather, wind, etc.)
 * - Zone 4 (Night): 9 types (stars, moon, moth, etc.)
 * - Zone 5 (Cave): 8 types (coins, gems, trophies, etc.)
 * - Zone 6 (Space): 9 types (energy orbs, portals, etc.)
 * Total: 62 decoration types
 */

import Phaser from 'phaser';
import { TextureGenerator } from '@/utils/TextureGenerator';
import { DecorationDrawingHelpers } from './decorators/DecorationDrawingHelpers';
import { SeededRandom } from '@/utils/SeededRandom';

export class DecorationTextureGenerator {
  private static readonly TEXTURE_SIZE = 128; // Larger than collectibles for complex decorations

  /**
   * Generate all decoration textures
   * Call this once at boot time (BootScene or GameScene.create())
   */
  static generateAllTextures(scene: Phaser.Scene): void {
    console.log('🎨 [TextureGen] Generating decoration textures...');
    const startTime = Date.now();

    // Zone 1 - Forest decorations
    this.generateDecorationTexture(scene, 'tiny-flower');
    this.generateDecorationTexture(scene, 'small-frog');
    this.generateDecorationTexture(scene, 'firefly');
    this.generateDecorationTexture(scene, 'dewdrop');
    this.generateDecorationTexture(scene, 'fallen-leaf');
    this.generateDecorationTexture(scene, 'mushroom-buddy');
    this.generateDecorationTexture(scene, 'moss-patch');
    this.generateDecorationTexture(scene, 'acorn');
    this.generateDecorationTexture(scene, 'small-twig');
    this.generateDecorationTexture(scene, 'grass-tuft');
    this.generateDecorationTexture(scene, 'tiny-snail');
    this.generateDecorationTexture(scene, 'beetle');
    this.generateDecorationTexture(scene, 'spider-web');
    this.generateDecorationTexture(scene, 'butterfly');
    this.generateDecorationTexture(scene, 'hanging-roots');
    this.generateDecorationTexture(scene, 'hanging-branch');
    this.generateDecorationTexture(scene, 'spider-on-web');
    this.generateDecorationTexture(scene, 'caterpillar');
    this.generateDecorationTexture(scene, 'shelf-mushroom');

    // Zone 2 - Trees decorations
    this.generateDecorationTexture(scene, 'small-bird');
    this.generateDecorationTexture(scene, 'bird-nest');
    this.generateDecorationTexture(scene, 'berries');
    this.generateDecorationTexture(scene, 'moth');
    this.generateDecorationTexture(scene, 'tree-moss');
    this.generateDecorationTexture(scene, 'hanging-vine');
    this.generateDecorationTexture(scene, 'hanging-tendril');
    this.generateDecorationTexture(scene, 'hanging-leaves');

    // Zone 3 - Sky decorations
    this.generateDecorationTexture(scene, 'cloud-puff');
    this.generateDecorationTexture(scene, 'flying-bird');
    this.generateDecorationTexture(scene, 'sky-butterfly');
    this.generateDecorationTexture(scene, 'floating-feather');
    this.generateDecorationTexture(scene, 'wind-swirl');
    this.generateDecorationTexture(scene, 'hanging-liana');
    this.generateDecorationTexture(scene, 'hanging-leaves-dense');

    // Zone 4 - Night decorations
    this.generateDecorationTexture(scene, 'twinkling-star');
    this.generateDecorationTexture(scene, 'crescent-moon');
    this.generateDecorationTexture(scene, 'shooting-star');
    this.generateDecorationTexture(scene, 'star-dust');
    this.generateDecorationTexture(scene, 'night-moth');
    this.generateDecorationTexture(scene, 'icicle');
    this.generateDecorationTexture(scene, 'water-drops');
    this.generateDecorationTexture(scene, 'mini-waterfall');

    // Zone 5 - Cave decorations
    this.generateDecorationTexture(scene, 'gold-coin');
    this.generateDecorationTexture(scene, 'gem-crystal');
    this.generateDecorationTexture(scene, 'golden-sparkle');
    this.generateDecorationTexture(scene, 'trophy-piece');
    this.generateDecorationTexture(scene, 'crown-fragment');
    this.generateDecorationTexture(scene, 'dripping-lava');
    this.generateDecorationTexture(scene, 'smoke-puffs');

    // Zone 6 - Space decorations
    this.generateDecorationTexture(scene, 'energy-orb');
    this.generateDecorationTexture(scene, 'rainbow-shard');
    this.generateDecorationTexture(scene, 'cosmic-particle');
    this.generateDecorationTexture(scene, 'portal-fragment');
    this.generateDecorationTexture(scene, 'lightning-bolt');
    this.generateDecorationTexture(scene, 'energy-tendril');

    const duration = Date.now() - startTime;
    console.log(`✅ [TextureGen] Generated 58 decoration textures in ${duration}ms`);
  }

  /**
   * Generate a single decoration texture
   */
  private static generateDecorationTexture(scene: Phaser.Scene, type: string): void {
    const textureKey = this.getTextureKey(type);
    const size = this.TEXTURE_SIZE;

    // Create a deterministic random for consistent texture generation
    const random = new SeededRandom(this.getTypeSeed(type));

    TextureGenerator.generateTexture(scene, textureKey, size, size, graphics => {
      // CRITICAL FIX: Draw decorations CENTERED in texture (not offset)
      // The sprite positioning will handle all offsets - texture should just contain
      // the decoration visual centered at (0,0)
      const centerX = size / 2;
      const centerY = size / 2;

      // Translate graphics to center of texture
      graphics.translateCanvas(centerX, centerY);

      // Draw the decoration centered at (0,0) without position offsets
      // Pass width=0, height=0 to minimize position calculations
      this.drawDecoration(graphics, type, random, 0, 0);
    });
  }

  /**
   * Draw a decoration using the existing DecorationDrawingHelpers
   */
  private static drawDecoration(
    graphics: Phaser.GameObjects.Graphics,
    type: string,
    random: SeededRandom,
    width: number,
    height: number
  ): void {
    // Map decoration types to drawing helpers
    switch (type) {
      // Zone 1 - Forest
      case 'tiny-flower':
        DecorationDrawingHelpers.drawTinyFlower(graphics, random, width, height);
        break;
      case 'small-frog':
        DecorationDrawingHelpers.drawSmallFrog(graphics, random, width, height);
        break;
      case 'firefly':
        DecorationDrawingHelpers.drawFirefly(graphics, random, width, height);
        break;
      case 'dewdrop':
        DecorationDrawingHelpers.drawDewdrop(graphics, random, width, height);
        break;
      case 'fallen-leaf':
        DecorationDrawingHelpers.drawFallenLeaf(graphics, random, width, height);
        break;
      case 'mushroom-buddy':
        DecorationDrawingHelpers.drawMushroomBuddy(graphics, random, width, height);
        break;
      case 'moss-patch':
        DecorationDrawingHelpers.drawMossPatch(graphics, random, width, height);
        break;
      case 'acorn':
        DecorationDrawingHelpers.drawAcorn(graphics, random, width, height);
        break;
      case 'small-twig':
        DecorationDrawingHelpers.drawSmallTwig(graphics, random, width, height);
        break;
      case 'grass-tuft':
        DecorationDrawingHelpers.drawGrassTuft(graphics, random, width, height);
        break;
      case 'tiny-snail':
        DecorationDrawingHelpers.drawTinySnail(graphics, random, width, height);
        break;
      case 'beetle':
        DecorationDrawingHelpers.drawBeetle(graphics, random, width, height);
        break;
      case 'spider-web':
        DecorationDrawingHelpers.drawSpiderWeb(graphics, random, width, height);
        break;
      case 'butterfly':
        DecorationDrawingHelpers.drawButterfly(graphics, random, width, height);
        break;
      case 'hanging-roots':
        DecorationDrawingHelpers.drawHangingRoots(graphics, random, width, height);
        break;
      case 'hanging-branch':
        DecorationDrawingHelpers.drawHangingBranch(graphics, random, width, height);
        break;
      case 'spider-on-web':
        DecorationDrawingHelpers.drawSpiderOnWeb(graphics, random, width, height);
        break;
      case 'caterpillar':
        DecorationDrawingHelpers.drawCaterpillar(graphics, random, width, height);
        break;
      case 'shelf-mushroom':
        DecorationDrawingHelpers.drawShelfMushroom(graphics, random, width, height);
        break;

      // Zone 2 - Trees
      case 'small-bird':
        DecorationDrawingHelpers.drawSmallBird(graphics, random, width, height);
        break;
      case 'bird-nest':
        DecorationDrawingHelpers.drawBirdNest(graphics, random, width, height);
        break;
      case 'berries':
        DecorationDrawingHelpers.drawBerries(graphics, random, width, height);
        break;
      case 'moth':
        DecorationDrawingHelpers.drawMoth(graphics, random, width, height);
        break;
      case 'tree-moss':
        DecorationDrawingHelpers.drawTreeMoss(graphics, random, width, height);
        break;
      case 'hanging-vine':
        DecorationDrawingHelpers.drawHangingVine(graphics, random, width, height);
        break;
      case 'hanging-tendril':
        DecorationDrawingHelpers.drawHangingTendril(graphics, random, width, height);
        break;
      case 'hanging-leaves':
        DecorationDrawingHelpers.drawHangingLeaves(graphics, random, width, height);
        break;

      // Zone 3 - Sky
      case 'cloud-puff':
        DecorationDrawingHelpers.drawCloudPuff(graphics, random, width, height);
        break;
      case 'flying-bird':
        DecorationDrawingHelpers.drawFlyingBird(graphics, random, width, height);
        break;
      case 'sky-butterfly':
        DecorationDrawingHelpers.drawSkyButterfly(graphics, random, width, height);
        break;
      case 'floating-feather':
        DecorationDrawingHelpers.drawFloatingFeather(graphics, random, width, height);
        break;
      case 'wind-swirl':
        DecorationDrawingHelpers.drawWindSwirl(graphics, random, width, height);
        break;
      case 'hanging-liana':
        DecorationDrawingHelpers.drawHangingLiana(graphics, random, width, height);
        break;
      case 'hanging-leaves-dense':
        DecorationDrawingHelpers.drawHangingLeavesDense(graphics, random, width, height);
        break;

      // Zone 4 - Night
      case 'twinkling-star':
        DecorationDrawingHelpers.drawTwinklingStar(graphics, random, width, height);
        break;
      case 'crescent-moon':
        DecorationDrawingHelpers.drawCrescentMoon(graphics, random, width, height);
        break;
      case 'shooting-star':
        DecorationDrawingHelpers.drawShootingStar(graphics, random, width, height);
        break;
      case 'star-dust':
        DecorationDrawingHelpers.drawStarDust(graphics, random, width, height);
        break;
      case 'night-moth':
        DecorationDrawingHelpers.drawNightMoth(graphics, random, width, height);
        break;
      case 'icicle':
        DecorationDrawingHelpers.drawIcicle(graphics, random, width, height);
        break;
      case 'water-drops':
        DecorationDrawingHelpers.drawWaterDrops(graphics, random, width, height);
        break;
      case 'mini-waterfall':
        DecorationDrawingHelpers.drawMiniWaterfall(graphics, random, width, height);
        break;

      // Zone 5 - Cave
      case 'gold-coin':
        DecorationDrawingHelpers.drawGoldCoin(graphics, random, width, height);
        break;
      case 'gem-crystal':
        DecorationDrawingHelpers.drawGemCrystal(graphics, random, width, height);
        break;
      case 'golden-sparkle':
        DecorationDrawingHelpers.drawGoldenSparkle(graphics, random, width, height);
        break;
      case 'trophy-piece':
        DecorationDrawingHelpers.drawTrophyPiece(graphics, random, width, height);
        break;
      case 'crown-fragment':
        DecorationDrawingHelpers.drawCrownFragment(graphics, random, width, height);
        break;
      case 'dripping-lava':
        DecorationDrawingHelpers.drawDrippingLava(graphics, random, width, height);
        break;
      case 'smoke-puffs':
        DecorationDrawingHelpers.drawSmokePuffs(graphics, random, width, height);
        break;

      // Zone 6 - Space
      case 'energy-orb':
        DecorationDrawingHelpers.drawEnergyOrb(graphics, random, width, height);
        break;
      case 'rainbow-shard':
        DecorationDrawingHelpers.drawRainbowShard(graphics, random, width, height);
        break;
      case 'cosmic-particle':
        DecorationDrawingHelpers.drawCosmicParticle(graphics, random, width, height);
        break;
      case 'portal-fragment':
        DecorationDrawingHelpers.drawPortalFragment(graphics, random, width, height);
        break;
      case 'lightning-bolt':
        DecorationDrawingHelpers.drawLightningBolt(graphics, random, width, height);
        break;
      case 'energy-tendril':
        DecorationDrawingHelpers.drawEnergyTendril(graphics, random, width, height);
        break;

      default:
        // Fallback: draw a simple placeholder
        graphics.fillStyle(0xff00ff, 1);
        graphics.fillCircle(0, 0, 12);
        break;
    }
  }

  /**
   * Get texture key for a decoration type
   */
  static getTextureKey(type: string): string {
    return `decoration_${type}`;
  }

  /**
   * Get deterministic seed for a decoration type
   */
  private static getTypeSeed(type: string): number {
    // Convert type string to a deterministic seed
    let seed = 0;
    for (let i = 0; i < type.length; i++) {
      seed = (seed << 5) - seed + type.charCodeAt(i);
      seed = seed & seed; // Convert to 32-bit integer
    }
    return Math.abs(seed);
  }

  /**
   * Clear all decoration textures
   */
  static clearTextures(scene: Phaser.Scene): void {
    // List all decoration types
    const decorationTypes = [
      // Zone 1
      'tiny-flower',
      'small-frog',
      'firefly',
      'dewdrop',
      'fallen-leaf',
      'mushroom-buddy',
      'moss-patch',
      'acorn',
      'small-twig',
      'grass-tuft',
      'tiny-snail',
      'beetle',
      'spider-web',
      'butterfly',
      'hanging-roots',
      'hanging-branch',
      'spider-on-web',
      'caterpillar',
      'shelf-mushroom',
      // Zone 2
      'small-bird',
      'bird-nest',
      'berries',
      'moth',
      'tree-moss',
      'hanging-vine',
      'hanging-tendril',
      'hanging-leaves',
      // Zone 3
      'cloud-puff',
      'flying-bird',
      'sky-butterfly',
      'floating-feather',
      'wind-swirl',
      'hanging-liana',
      'hanging-leaves-dense',
      // Zone 4
      'twinkling-star',
      'crescent-moon',
      'shooting-star',
      'star-dust',
      'night-moth',
      'icicle',
      'water-drops',
      'mini-waterfall',
      // Zone 5
      'gold-coin',
      'gem-crystal',
      'golden-sparkle',
      'trophy-piece',
      'crown-fragment',
      'dripping-lava',
      'smoke-puffs',
      // Zone 6
      'energy-orb',
      'rainbow-shard',
      'cosmic-particle',
      'portal-fragment',
      'lightning-bolt',
      'energy-tendril',
    ];

    const keys = decorationTypes.map(type => this.getTextureKey(type));
    TextureGenerator.clearTextures(scene, keys);
  }
}
